/**
 * Renders a trip SNAPSHOT — the thing an invited person sees.
 *
 * Used in two places, deliberately the same component in both:
 *   1. the owner's "Preview" dialog, before they finalise
 *   2. the public /trip/:token page
 *
 * If those were two components they would drift, and the preview would stop
 * being a preview. The owner is shown exactly what their guests are shown.
 *
 * It reads only the snapshot. It does no Firestore work, takes no context, and
 * knows nothing about who is looking — which is what makes it safe to render
 * for an unauthenticated visitor.
 */

import React from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import HotelRoundedIcon from '@mui/icons-material/HotelRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';

function formatDate(millis) {
  if (!Number.isFinite(millis)) return '';
  return new Date(millis).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatRange(dates) {
  const list = (dates || []).filter(Number.isFinite);
  if (!list.length) return '';
  const start = formatDate(list[0]);
  const end = formatDate(list[list.length - 1]);
  return start === end ? start : `${start} - ${end}`;
}

function formatMoney(value, currency) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(
      Number(value) || 0
    );
  } catch {
    return `${currency || 'USD'} ${(Number(value) || 0).toFixed(2)}`;
  }
}

function travellerLine(travelers) {
  const parts = [];
  const { adults = 0, children = 0, infants = 0 } = travelers || {};
  if (adults) parts.push(`${adults} ${adults === 1 ? 'adult' : 'adults'}`);
  if (children) parts.push(`${children} ${children === 1 ? 'child' : 'children'}`);
  if (infants) parts.push(`${infants} ${infants === 1 ? 'infant' : 'infants'}`);
  return parts.join(' · ');
}

export default function TripPreview({ snapshot, dense = false }) {
  if (!snapshot) return null;

  const range = formatRange(snapshot.dates);
  const travellers = travellerLine(snapshot.travelers);
  const isBooking = snapshot.tripType === 'BOOKING';

  return (
    <Box>
      <Paper elevation={dense ? 0 : 1} sx={{ p: dense ? 0 : 2, mb: 2, background: 'transparent' }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {snapshot.title}
        </Typography>

        {range && (
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {range}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
          {snapshot.location && (
            <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
              <LocationOnRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.5, color: 'text.secondary' }} />
              {snapshot.location}
            </Typography>
          )}
          {snapshot.vacationtype && (
            <Chip size="small" color="primary" variant="outlined" label={snapshot.vacationtype} />
          )}
          {snapshot.typeoftravelers && (
            <Chip size="small" variant="outlined" label={snapshot.typeoftravelers} />
          )}
          {isBooking && snapshot.status && (
            <Chip size="small" variant="outlined" label={snapshot.status} />
          )}
        </Box>

        {travellers && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
          >
            <PeopleRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />
            {travellers}
          </Typography>
        )}

        {snapshot.accommodation?.name && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HotelRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />
            Staying at {snapshot.accommodation.name}
          </Typography>
        )}

        {isBooking && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            Total: {formatMoney(snapshot.totalPaid, snapshot.currency)}
          </Typography>
        )}
      </Paper>

      {!isBooking && (
        <Box>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 1 }}
          >
            <EventNoteRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.75, color: 'text.secondary' }} />
            Day plan
          </Typography>

          {(snapshot.schedule || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No days to show yet.
            </Typography>
          ) : (
            snapshot.schedule.map((day) => (
              <Paper key={`day-${day.day}`} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Day {day.day}</Typography>

                {(day.slots || []).map((slot, i) => (
                  <Box key={`slot-${day.day}-${i}`} sx={{ mb: i === day.slots.length - 1 ? 0 : 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {slot.dayTime}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {slot.time}
                      </Typography>
                    </Box>

                    {slot.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {slot.description}
                      </Typography>
                    )}

                    {!!slot.restaurants?.length && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Restaurants: {slot.restaurants.join(', ')}
                      </Typography>
                    )}
                    {!!slot.activities?.length && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Things to do: {slot.activities.join(', ')}
                      </Typography>
                    )}

                    {i !== day.slots.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                  </Box>
                ))}
              </Paper>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}
